import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link, useOutletContext } from "react-router-dom";
import {
  Building2, CheckCircle2, Ban, Inbox, Clock, Trash2, ExternalLink, MapPin, Users, BadgeCheck, AlertTriangle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", icon: Ban },
  { key: "all", label: "All", icon: Inbox },
];

const money = (n) => `K${Number(n || 0).toLocaleString("en-ZM", { maximumFractionDigits: 0 })}`;

export default function AdminListings() {
  const ctx = useOutletContext();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = tab === "all" ? {} : { params: { status: tab } };
      const { data } = await axios.get(`${API}/admin/properties`, params);
      setItems(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    try {
      const { data } = await axios.get(`${API}/admin/properties/counts`);
      setCounts(data);
      ctx?.refreshPendingListings?.();
    } catch { /* non-critical */ }
  }, [tab, ctx]);
  useEffect(() => { load(); }, [load]);

  const approve = async (p) => {
    if (!window.confirm(
      `Approve "${p.title}"?\n\nBy approving you confirm this listing meets EasyStay's listing standards. It will be visible to guests immediately.`
    )) return;
    setBusyId(p.id);
    try {
      await axios.post(`${API}/admin/properties/${p.id}/approve`);
      toast.success(`Approved · "${p.title}" is now live`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const openReject = (p) => { setRejecting(p); setRejectReason(""); };
  const closeReject = () => { setRejecting(null); setRejectReason(""); };
  const confirmReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    try {
      await axios.post(`${API}/admin/properties/${rejecting.id}/reject`, { reason: rejectReason });
      toast.success("Rejected · host has been notified");
      closeReject(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  const openDelete = (p) => setDeleting(p);
  const closeDelete = () => setDeleting(null);
  const confirmDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      await axios.delete(`${API}/admin/properties/${deleting.id}`);
      toast.success("Listing deleted permanently");
      closeDelete(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Listings moderation</h1>
          <p className="mt-1 text-sm text-slate-600">
            Verified hosts auto-approve; unverified submissions arrive here for review.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const c = t.key === "all" ? counts.total : counts[t.key];
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`listings-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold transition ${
                active ? "border-copper text-copper" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] ${active ? "bg-copper/10 text-copper" : "bg-slate-100 text-slate-600"}`}>{c ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-4">
        {items.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 text-center card-shadow">
            <Building2 className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-slate-500">No {tab === "all" ? "" : tab} listings.</p>
          </div>
        ) : items.map((p) => {
          const status = p.status || "approved";
          const statusClass =
            status === "pending" ? "bg-amber-100 text-amber-800 ring-amber-200"
            : status === "approved" ? "bg-green-100 text-green-800 ring-green-200"
            : "bg-rose-100 text-rose-800 ring-rose-200";
          return (
            <div key={p.id} data-testid={`listing-row-${p.id}`} className="rounded-3xl bg-white p-5 card-shadow">
              <div className="flex flex-wrap items-start gap-4">
                {p.photos?.[0] ? (
                  <img src={p.photos[0]} alt="" className="h-24 w-32 flex-shrink-0 rounded-2xl object-cover" />
                ) : (
                  <div className="flex h-24 w-32 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-100">
                    <Building2 className="h-8 w-8 text-slate-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span data-testid={`listing-status-${p.id}`}
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ring-1 ${statusClass}`}>
                      {status}
                    </span>
                    <span className="chip !bg-slate-100 !text-slate-700 capitalize">{p.type?.replace("_", " ")}</span>
                    <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.city}</span>
                  </div>
                  <h3 className="mt-1 font-display text-lg font-bold text-slate-900">
                    <Link to={`/property/${p.id}`} target="_blank" className="hover:underline">{p.title}</Link>
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 line-clamp-2">{p.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-slate-500">Host: <span className="font-semibold text-slate-900">{p.host_name || "—"}</span>
                      {p.host_verified && <BadgeCheck className="ml-1 inline h-3.5 w-3.5 fill-copper text-white" />}
                    </span>
                    {p.host_email && <a href={`mailto:${p.host_email}`} className="text-copper hover:underline">{p.host_email}</a>}
                    <span className="text-slate-500">Nightly: <b className="text-slate-900">{money(p.price_per_night_zmw)}</b></span>
                    <span className="text-slate-500">Hourly: <b className="text-slate-900">{money(p.price_per_hour_zmw)}</b></span>
                    <span className="text-slate-500 flex items-center gap-1"><Users className="h-3 w-3" /> {p.max_guests}</span>
                  </div>
                  {status === "rejected" && p.rejection_reason && (
                    <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      <b>Rejection reason:</b> {p.rejection_reason}
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 flex-wrap gap-2">
                  <Link to={`/property/${p.id}`} target="_blank"
                    data-testid={`view-listing-${p.id}`}
                    className="btn-outline !py-2 text-xs">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  {status === "pending" && (
                    <>
                      <button onClick={() => approve(p)} disabled={busyId === p.id}
                        data-testid={`approve-listing-${p.id}`}
                        className="btn-primary !py-2 text-xs !bg-green-600 hover:!bg-green-700">
                        <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Approve
                      </button>
                      <button onClick={() => openReject(p)} disabled={busyId === p.id}
                        data-testid={`reject-listing-${p.id}`}
                        className="btn-outline !py-2 text-xs !border-rose-200 !text-rose-600">
                        <Ban className="mr-1 inline h-3.5 w-3.5" /> Reject
                      </button>
                    </>
                  )}
                  {status === "rejected" && (
                    <button onClick={() => approve(p)} disabled={busyId === p.id}
                      data-testid={`reapprove-listing-${p.id}`}
                      className="btn-outline !py-2 text-xs">
                      <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Re-approve
                    </button>
                  )}
                  <button onClick={() => openDelete(p)} disabled={busyId === p.id}
                    data-testid={`delete-listing-${p.id}`}
                    className="btn-outline !py-2 text-xs !border-rose-200 !text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && closeReject()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reject listing · {rejecting?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              The host receives your reason by email so they can fix and resubmit.
            </p>
            <textarea
              data-testid="reject-listing-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              placeholder="e.g. Photos are low quality, address is incomplete, price does not match category…"
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
            />
          </div>
          <DialogFooter>
            <button onClick={closeReject} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={confirmReject}
              disabled={busyId === rejecting?.id}
              data-testid="confirm-reject-listing-btn"
              className="btn-primary text-sm !bg-rose-600 hover:!bg-rose-700"
            >
              {busyId === rejecting?.id ? "Rejecting…" : "Reject & notify"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && closeDelete()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete listing permanently?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <div className="flex items-start gap-2 rounded-xl bg-rose-50 p-3 text-rose-800">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                This will permanently remove <b>{deleting?.title}</b> and any historical bookings will lose their linked listing metadata.
                Use <b>Reject</b> instead if you want to give the host a chance to resubmit.
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={closeDelete} className="btn-outline text-sm">Cancel</button>
            <button
              onClick={confirmDelete}
              disabled={busyId === deleting?.id}
              data-testid="confirm-delete-listing-btn"
              className="btn-primary text-sm !bg-rose-600 hover:!bg-rose-700"
            >
              {busyId === deleting?.id ? "Deleting…" : "Delete permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
